import React, { useState, useEffect, useCallback, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useParams, useLocation } from 'react-router-dom';
import { track } from '@vercel/analytics';
import { errorTrackingService } from '@/services/errorTrackingService';
import { monitoringService } from '@/services/monitoringService';
import MainScreen from '@/components/MainScreen';
import EnhancedGameplayScreen from '@/components/EnhancedGameplayScreen';
import LoadingIndicator from '@/components/LoadingIndicator';
import UserProfileScreen from '@/components/UserProfileScreen';
import AuthScreen from '@/components/AuthScreen';
import LobbyScreen from '@/components/LobbyScreen';
import CartridgeBrowserScreen from '@/components/CartridgeBrowserScreen';
import CartridgeEditorScreen from '@/components/CartridgeEditorScreen';
import RoomBrowserScreen from '@/components/RoomBrowserScreen';
import ModeSelectionScreen from '@/components/ModeSelectionScreen';
import { CombatScreen } from '@/components/CombatScreen';
import { SaveIndicator } from './components/SaveIndicator';
import GraphicalMode from '@/components/GraphicalMode';
import { isFeatureEnabled } from '@/utils/featureFlags';
const TradingModal = React.lazy(() => import('@/components/trading').then(m => ({ default: m.TradingModal })));
const CharacterChatPanel = React.lazy(() => import('@/components/CharacterChatPanel').then(m => ({ default: m.CharacterChatPanel })));
import { 
  GameState,
  Archetype, StorySegment, ChatMessage, 
  UserProfile,
  PlayerState,
  GameCartridge,
  MultiplayerRoom,
  CompanionMessage
} from '@/types';
// Migration: Using ElevenLabs adapter instead of OpenRouter
import { generateStorySegment, generateImage, generateChatResponse } from '@/services/openRouterToElevenLabsAdapter';
import { speechService } from '@/services/speechService';
import { contentModerationService } from '@/services/contentModerationService';
import { MessageDeduplicationManager } from '@/utils/messageDeduplication';
import { 
  INITIAL_NARRATIVE_PROMPT_BASE, 
  MAX_HISTORY_ITEMS,
  DEFAULT_PLAYER_NAME,
  ARCHETYPES,
  CHARACTER_TRAITS,
  MINDSETS
} from '@/constants';
import { fillUserProfilePlaceholders } from '@/services/openRouterToElevenLabsAdapter';
import { authService } from '@/services/authService';
import { databaseService } from '@/services/databaseService';
import { storageService } from '@/services/storageService';
import { supabaseClient } from '@/services/supabaseClient';
import type { User } from '@supabase/supabase-js';
import { CreateRoomParameters } from '@/components/RoomBrowserScreen';
import { multiplayerStoryService } from '@/services/multiplayerStoryService';
import { playerProgressService } from '@/services/playerProgressService';
import { decisionTreeService } from '@/services/decisionTreeService';
import { clientErrorService } from '@/services/clientErrorService';
import { SentryTest } from '@/components/SentryTest';
import { ContinueOrNewDialog } from '@/components/ContinueOrNewDialog';
import GlobalChatDock from '@/components/GlobalChatDock';
import { DebugOpenRouter } from '@/components/DebugOpenRouter';
import { AutonomyConsole } from '@/components/AutonomyConsole';
import { autonomousEcosystemService } from '@/services/autonomousEcosystemService';
import { sessionPersistenceService } from '@/services/sessionPersistenceService';
import { useGameStore } from '@/state/gameStore';
import { levelUpService } from '@/services/levelUpService';
import { soundscapeService } from '@/services/soundscapeService';
import { chatTranslationService } from '@/services/chatTranslationService';
import { safeTTSService } from '@/services/safeTTSService';

const App: React.FC = () => {
  const location = useLocation();
  const [supabaseUser, setSupabaseUser] = useState<User | null>(null);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);
  const [isProfileLoadingState, setIsProfileLoadingState] = useState(false);
  const [isHandlingAuth, setIsHandlingAuth] = useState(false);
  const [authTimeoutId, setAuthTimeoutId] = useState<ReturnType<typeof setTimeout> | null>(null);
  const [lastAuthEvent, setLastAuthEvent] = useState<{ type: string; userId: string; timestamp: number } | null>(null);
  const [pendingRoomId, setPendingRoomId] = useState<string | null>(null);
  const gameState = useGameStore((state) => state);
  const setGameState = useGameStore((state) => state.setGameState);

  // Trading modal state
  const [showTradingModal, setShowTradingModal] = useState(false);

  // Character chat state
  const [showCharacterChat, setShowCharacterChat] = useState(false);

  // Save state tracking
  const [saveState, setSaveState] = useState<{
    isSaving: boolean;
    lastSaved: Date | null;
    error: string | null;
  }>({
    isSaving: false,
    lastSaved: null,
    error: null,
  });

  // Auto-save interval reference
  const autoSaveInterval = useRef<ReturnType<typeof setInterval> | null>(null);
  
  // Autonomous ecosystem state
  const [ecosystemActive, setEcosystemActive] = useState(false);

  // State persistence helpers
  const STORAGE_VERSION = '1.1';
  let saveTimeout: ReturnType<typeof setTimeout> | null = null;
  
  const saveGameStateToStorage = (state: GameState) => {
    // Debounce saves to prevent excessive writes
    if (saveTimeout) {
      clearTimeout(saveTimeout);
    }
    
    saveTimeout = setTimeout(async () => {
      try {
        // Save to localStorage as a backup
        const stateToSave = {
          version: STORAGE_VERSION,
          timestamp: Date.now(),
          data: {
            currentScreen: state.currentScreen,
            userProfile: state.userProfile,
            currentPlayerState: state.currentPlayerState,
            selectedCartridge: state.selectedCartridge,
            currentRoom: state.currentRoom,
            isMultiplayer: state.isMultiplayer,
            chatMessages: state.chatMessages,
            currentStorySegment: state.currentStorySegment,
            currentSessionId: state.currentSessionId
          }
        };
        localStorage.setItem('lucidCyberRPG_gameState', JSON.stringify(stateToSave));
        console.log('[App] Game state saved to localStorage backup');

        // Save to Supabase
        if (state.currentScreen === 'gameplay' && state.userProfile && state.selectedCartridge) {
          await handleSaveGame();
        }
      } catch (error) {
        console.warn('[App] Failed to save game state:', error);
      }
    }, 1500); // Debounce for 1.5 seconds
  };

  const loadGameStateFromStorage = (): Partial<GameState> | null => {
    try {
      const savedState = localStorage.getItem('lucidCyberRPG_gameState');
      if (savedState) {
        const parsed = JSON.parse(savedState);
        
        // Check version compatibility
        if (parsed.version !== STORAGE_VERSION) {
          console.warn('[App] Saved state version mismatch, clearing old data');
          localStorage.removeItem('lucidCyberRPG_gameState');
          return null;
        }
        
        // Check if the data is too old (more than 24 hours)
        if (parsed.timestamp && Date.now() - parsed.timestamp > 24 * 60 * 60 * 1000) {
          console.warn('[App] Saved state is too old, clearing');
          localStorage.removeItem('lucidCyberRPG_gameState');
          return null;
        }
        
        console.log('[App] Game state loaded from localStorage');
        return parsed.data || parsed; // Support both old and new format
      }
    } catch (error) {
      console.warn('[App] Failed to load game state:', error);
      localStorage.removeItem('lucidCyberRPG_gameState'); // Clear corrupted data
    }
    return null;
  };

  const clearPersistedState = () => {
    localStorage.removeItem('lucidCyberRPG_gameState');
    console.log('[App] Persisted game state cleared');
  };

  // Save state when important changes occur with debouncing
  useEffect(() => {
    // Only save if we're in a meaningful state
    if (gameState.currentScreen !== 'auth' && gameState.userProfile) {
      const debounceTimer = setTimeout(() => {
        saveGameStateToStorage(gameState);
        console.log('[App] Auto-saved game state');
      }, 1000); // 1 second debounce

      return () => clearTimeout(debounceTimer);
    }
  }, [
    gameState.currentScreen,
    gameState.userProfile,
    gameState.currentPlayerState,
    gameState.currentStorySegment,
    gameState.chatMessages,
    gameState.selectedCartridge,
    gameState.currentRoom,
    gameState.currentSessionId,
    gameState.sessionId
  ]);

  // Auto-save interval for gameplay
  useEffect(() => {
    // Clear any existing interval
    if (autoSaveInterval.current) {
      clearInterval(autoSaveInterval.current);
      autoSaveInterval.current = null;
    }

    // Set up auto-save only during gameplay
    if (gameState.currentScreen === 'gameplay' && gameState.currentPlayerState) {
      console.log('[App] Setting up auto-save interval');
      
      // Save immediately for testing
      if (process.env.NODE_ENV === 'development') {
        console.log('[App] Triggering immediate save for testing...');
        setTimeout(() => {
          handleSaveGame();
        }, 5000); // Save after 5 seconds for testing
      }
      
      autoSaveInterval.current = setInterval(() => {
        console.log('[App] Auto-saving game...');
        handleSaveGame();
      }, 3 * 60 * 1000); // Save every 3 minutes
    }

    // Cleanup on unmount or screen change
    return () => {
      if (autoSaveInterval.current) {
        clearInterval(autoSaveInterval.current);
        autoSaveInterval.current = null;
      }
    };
  }, [gameState.currentScreen, gameState.currentPlayerState]);

  // Track screen changes and manage ecosystem
  useEffect(() => {
    errorTrackingService.setScreen(gameState.currentScreen);
    errorTrackingService.logAction('screen_change', { 
      screen: gameState.currentScreen,
      hasProfile: !!gameState.userProfile,
      isMultiplayer: gameState.isMultiplayer
    });
    
    // Track in monitoring service
    monitoringService.trackEvent('screen_view', {
      screen: gameState.currentScreen,
      hasProfile: !!gameState.userProfile,
      isMultiplayer: gameState.isMultiplayer,
      archetype: gameState.currentPlayerState?.archetype?.name,
      cartridge: gameState.selectedCartridge?.name
    });
    
    // Manage autonomous ecosystem based on screen
    if (gameState.currentScreen === 'gameplay' && gameState.userProfile) {
      if (!ecosystemActive) {
        console.log('[App] Starting autonomous ecosystem for gameplay');
        autonomousEcosystemService.startEcosystem();
        setEcosystemActive(true);
        
        // Start contextual soundscape
        const context = {
          location: gameState.currentPlayerState?.location || 'neo-tokyo-plaza',
          timeOfDay: 'day' as const,
          weather: 'clear',
          mood: 'calm' as const,
          worldKarma: gameState.worldKarma || 0
        };
        soundscapeService.generateContextualSoundscape(context);
      }
    } else {
      if (ecosystemActive) {
        console.log('[App] Stopping autonomous ecosystem');
        autonomousEcosystemService.stopEcosystem();
        setEcosystemActive(false);
        soundscapeService.emergencyStop();
      }
    }
  }, [gameState.currentScreen, gameState.userProfile, gameState.isMultiplayer, ecosystemActive]);

  // Handle page visibility changes (minimize/restore)
  const previousScreenRef = useRef<string>('');
  const currentScreenRef = useRef<string>('auth'); // Always tracks latest screen
  const isRestoringRef = useRef(false);

  // Keep ref in sync with state
  useEffect(() => {
    currentScreenRef.current = gameState.currentScreen;
  }, [gameState.currentScreen]);
  
  // Handle room join from URL
  useEffect(() => {
    // Check if we're on a game join route
    const pathParts = location.pathname.split('/');
    if (pathParts[1] === 'game' && pathParts[2] === 'join' && pathParts[3]) {
      const roomId = pathParts[3];
      console.log('[App] Room join URL detected:', roomId);
      setPendingRoomId(roomId);
      
      // If user is authenticated and has profile, join the room
      if (gameState.userProfile && supabaseUser) {
        handleJoinRoom(roomId);
      }
    }
  }, [location.pathname]);

  useEffect(() => {
    // Get URL parameters for invite codes
    const urlParams = new URLSearchParams(window.location.search);
    const inviteCode = urlParams.get('invite');
    
    const handleVisibilityChange = async () => {
      if (document.hidden) {
        // Page is being hidden - save current state
        // Use ref to get the ACTUAL latest screen (not stale closure value)
        const latestScreen = currentScreenRef.current;
        previousScreenRef.current = latestScreen;
        console.log('[App] Page hidden, saving screen:', latestScreen);
        saveGameStateToStorage(gameState);

        // Save comprehensive state for restoration including git state
        // Double-check if we're actually in gameplay by checking for game session indicators
        const actualScreen = gameState.currentSessionId || gameState.sessionId ||
                           (gameState.selectedCartridge && gameState.currentStorySegment) ?
                           'gameplay' : latestScreen;

        console.log('[App] Determining actual screen for backup:', {
          currentScreen: latestScreen,
          actualScreen,
          hasSessionId: !!(gameState.currentSessionId || gameState.sessionId),
          hasCartridge: !!gameState.selectedCartridge,
          hasStorySegment: !!gameState.currentStorySegment
        });

        const stateBackup = {
          currentScreen: actualScreen,
          selectedCartridge: gameState.selectedCartridge,
          roomCode: gameState.roomCode,
          isMultiplayer: gameState.isMultiplayer,
          currentStorySegment: gameState.currentStorySegment,
          sessionId: gameState.sessionId || gameState.currentSessionId,
          currentRoom: gameState.currentRoom,
          currentPlayerState: gameState.currentPlayerState,
          userProfile: gameState.userProfile,
          chatMessages: gameState.chatMessages,
          currentInstanceId: gameState.currentInstanceId,
          // Git-based game state
          currentBranchId: gameState.currentBranchId,
          worldKarma: gameState.worldKarma,
          karmaHistory: gameState.karmaHistory,
          // Year Zero Engine state
          yearZeroCharacter: gameState.yearZeroCharacter,
          currentStress: gameState.currentStress,
          activeMutations: gameState.activeMutations,
          // Additional gameplay state
          activeAgent: gameState.activeAgent,
          timestamp: Date.now()
        };
        localStorage.setItem('lucid_cyber_rpg_state_backup', JSON.stringify(stateBackup));
        
        // Save current screen separately for easier access
        const importantScreens = ['gameplay', 'main', 'graphical', 'cartridge_browser', 'cartridge_editor', 'room_browser', 'combat', 'mode_selection', 'continue_dialog'];
        if (importantScreens.includes(gameState.currentScreen)) {
          localStorage.setItem('lucid_cyber_rpg_last_screen', gameState.currentScreen);
          localStorage.setItem('lucid_cyber_rpg_last_screen_time', Date.now().toString());
        }
      } else {
        // Page is becoming visible again
        console.log('[App] Page visible again, checking auth state...');
        
        // First check if we still have a valid session
        const { data: { session } } = await supabaseClient.auth.getSession();
        if (!session) {
          console.log('[App] No session after visibility restore');
          return;
        }
        
        // Refresh the session to prevent expiry
        try {
          const { data, error } = await supabaseClient.auth.refreshSession();
          if (error) {
            console.error('[App] Session refresh error:', error);
            // If refresh fails, try to re-authenticate
            const { data: { user } } = await supabaseClient.auth.getUser();
            if (!user) {
              setGameState({ currentScreen: 'auth' });
              return;
            }
          } else {
            console.log('[App] Session refreshed successfully');
          }
        } catch (error) {
          console.error('[App] Visibility change error:', error);
        }
        
        // Check for state backup
        const stateBackupStr = localStorage.getItem('lucid_cyber_rpg_state_backup');
        if (!stateBackupStr) return;
        
        try {
          const stateBackup = JSON.parse(stateBackupStr);
          const timeSinceBackup = Date.now() - stateBackup.timestamp;
          
          // Only restore if backup is less than 30 minutes old
          if (timeSinceBackup < 1800000) {
            console.log('[App] Restoring state from backup:', stateBackup);
            
            // Restore the screen from backup, including gameplay
            let restoredScreen = stateBackup.currentScreen || gameState.currentScreen;
            
            // If we have a valid game session in the backup, ALWAYS restore to gameplay
            if (stateBackup.currentScreen === 'gameplay' ||
                (stateBackup.sessionId && stateBackup.selectedCartridge && stateBackup.currentStorySegment)) {
              restoredScreen = 'gameplay';
              console.log('[App] Restoring gameplay session from backup');
            }
            // Only use lobby if BOTH current AND backup screens are auth (truly starting fresh)
            else if (gameState.currentScreen === 'auth' && stateBackup.currentScreen === 'auth' && session) {
              restoredScreen = 'lobby';
              console.log('[App] Overriding auth screen with lobby since session is valid');
            }
            // Otherwise keep the backup screen as-is
            else {
              restoredScreen = stateBackup.currentScreen || gameState.currentScreen;
              console.log('[App] Keeping backup screen:', restoredScreen);
            }
            
            console.log('[App] Screen restoration:', { 
              current: gameState.currentScreen, 
              backup: stateBackup.currentScreen, 
              final: restoredScreen,
              hasCartridge: !!stateBackup.selectedCartridge,
              hasSession: !!stateBackup.sessionId
            });
            
            // Restore the comprehensive game state including git state
            setGameState((state) => ({
              currentScreen: restoredScreen,
              selectedCartridge: stateBackup.selectedCartridge || state.selectedCartridge,
              selectedArchetype: stateBackup.selectedArchetype || state.selectedArchetype, // CRITICAL: Restore archetype to prevent reset
              roomCode: stateBackup.roomCode || state.roomCode,
              isMultiplayer:
                stateBackup.isMultiplayer !== undefined ? stateBackup.isMultiplayer : state.isMultiplayer,
              currentStorySegment: stateBackup.currentStorySegment || state.currentStorySegment,
              currentSegmentIndex:
                stateBackup.currentSegmentIndex !== undefined
                  ? stateBackup.currentSegmentIndex
                  : state.currentSegmentIndex,
              storyHistory: stateBackup.storyHistory || state.storyHistory,
              sessionId: stateBackup.sessionId || state.sessionId,
              currentSessionId: stateBackup.sessionId || state.currentSessionId,
              currentRoom: stateBackup.currentRoom || state.currentRoom,
              currentPlayerState: stateBackup.currentPlayerState || state.currentPlayerState,
              userProfile: stateBackup.userProfile || state.userProfile,
              chatMessages: stateBackup.chatMessages || state.chatMessages,
              currentInstanceId: stateBackup.currentInstanceId || state.currentInstanceId,
              // Restore git-based game state
              currentBranchId: stateBackup.currentBranchId || state.currentBranchId,
              worldKarma:
                stateBackup.worldKarma !== undefined ? stateBackup.worldKarma : state.worldKarma,
              karmaHistory: stateBackup.karmaHistory || state.karmaHistory,
              // Restore Year Zero Engine state
              yearZeroCharacter: stateBackup.yearZeroCharacter || state.yearZeroCharacter,
              currentStress: stateBackup.currentStress || state.currentStress,
              activeMutations: stateBackup.activeMutations || state.activeMutations,
              // Restore additional gameplay state
              activeAgent: stateBackup.activeAgent || state.activeAgent,
              // Ensure proper loading states are reset
              isLoading: false,
              isLoadingChat: false,
              isListening: false,
              error: null,
            }));
            
            console.log('[App] Full game state restored successfully');
            
            // Clean up the backup after successful restoration
            localStorage.removeItem('lucid_cyber_rpg_state_backup');
          }
        } catch (error) {
          console.error('[App] Failed to restore state backup:', error);
        }
        
        // If we didn't already restore from the main backup, check for fallback scenarios
        if (!stateBackupStr) {
          // Check if we got dropped to auth or lobby unexpectedly
          const hasValidSession = supabaseUser || (await authService.getSession())?.data?.session;
          const lastScreen = localStorage.getItem('lucid_cyber_rpg_last_screen');
        const importantScreens = ['gameplay', 'graphical', 'main', 'cartridge_browser', 'cartridge_editor', 'room_browser', 'combat', 'mode_selection'];
          const wasInImportantScreen = previousScreenRef.current && importantScreens.includes(previousScreenRef.current);
          
          if (wasInImportantScreen && 
              (gameState.currentScreen === 'lobby' || gameState.currentScreen === 'auth') && 
              !isRestoringRef.current &&
              hasValidSession) {
            
            console.log('[App] Detected unwanted transition, attempting fallback restore:', lastScreen || previousScreenRef.current);
            isRestoringRef.current = true;
            
            // Try to restore from the main game state storage as fallback
            const restoredState = loadGameStateFromStorage();
            if (restoredState && restoredState.currentScreen && importantScreens.includes(restoredState.currentScreen)) {
              console.log('[App] Restoring full game state from fallback backup');
              setGameState(() => ({
                ...restoredState,
                isLoading: false,
                isLoadingChat: false,
                isListening: false,
                error: null,
              }));
              localStorage.removeItem('lucid_cyber_rpg_last_screen');
              localStorage.removeItem('lucid_cyber_rpg_last_screen_time');
            } else {
              console.warn('[App] No valid game state found in fallback backup to restore.');
            }

            // Clean up restoration flag after a delay
            setTimeout(() => {
              isRestoringRef.current = false;
            }, 500);
          }
        }
      }
    };

    const handleBeforeUnload = () => {
      saveGameStateToStorage(gameState);
    };

    // Page focus handler
    const handleFocus = async () => {
      const { data: { session } } = await supabaseClient.auth.getSession();
      if (session) {
        console.log('[App] Page focused, session active');
      }
    };

    // Add visibility and unload listeners
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('focus', handleFocus);
    
    // Listen for session refresh events from supabaseService
    const handleSessionRefreshed = (event: any) => {
      const user = event.detail?.user;
      if (user && supabaseUser?.id !== user.id) {
        console.log('[App] Session refreshed by supabaseService, updating user');
        setSupabaseUser(user);
      }
    };
    window.addEventListener('supabase-session-refreshed', handleSessionRefreshed);

    // Environment debugging and API key check
    const initializeEnvironment = async () => {
      try {
        // Import debugging utilities
        const { debugEnvironment, validateCriticalEnvVars } = await import('./utils/envDebug');
        
        // Debug environment variables
        debugEnvironment();
        
        // Validate critical environment variables
        const validation = validateCriticalEnvVars();
        if (!validation.valid) {
          console.error('[App] Missing critical environment variables:', validation.missing);
          
          // Show user-friendly error for missing API key
          if (validation.missing.includes('VITE_OPENROUTER_API_KEY')) {
            console.error('[App] OpenRouter API key is missing or invalid. This will cause AI generation to fail.');
          }
        }
        
        // API key check
        const apiKey = (import.meta as any).env.VITE_OPENROUTER_API_KEY;
        if (apiKey && apiKey.trim().length > 0) {
          console.log('✅ OpenRouter API key is configured');
          setGameState({ apiKeyStatus: 'exists' });
        } else {
          console.warn('⚠️ OpenRouter API key is not configured');
          setGameState({ apiKeyStatus: 'not_exists' });
        }
      } catch (error) {
        console.error('[App] Failed to initialize environment debugging:', error);
        // Fallback to simple check
        const apiKey = (import.meta as any).env.VITE_OPENROUTER_API_KEY;
        setGameState({
          apiKeyStatus: apiKey && apiKey.trim().length > 0 ? 'exists' : 'not_exists',
        });
      }
    };
    
    initializeEnvironment();

    // Initialize error monitoring
    clientErrorService.init();
    
    // Make it available globally for DXT monitoring
    (window as any).lucidMonitor = {
      getErrors: () => clientErrorService.getErrors(),
      getMonitoringData: () => clientErrorService.getMonitoringData()
    };
    
    // Initialize auth and try to restore state
    const initializeAuth = async () => {
      try {
        // First check if we have a saved state that might indicate we're just resuming
        const savedState = loadGameStateFromStorage();
        const lastScreen = localStorage.getItem('lucid_cyber_rpg_last_screen');
        const isResuming = savedState && savedState.userProfile && !document.hidden;
        
        // Check for pre-combat state if we're in combat
        if (savedState && savedState.currentScreen === 'combat') {
          const savedPreCombatState = localStorage.getItem('lucid_cyber_rpg_pre_combat_state');
          if (savedPreCombatState) {
            try {
              const preCombatState = JSON.parse(savedPreCombatState);
              savedState.preCombatState = preCombatState;
              console.log('[App] Restored pre-combat state from localStorage');
            } catch (e) {
              console.error('[App] Failed to parse pre-combat state:', e);
            }
          }
        }
        
        const { data: { session }, error } = await authService.getSession();
        
        if (error) {
          console.warn('[App] Session error:', error);
          // Only redirect to auth if we're not resuming from a saved state
          if (!isResuming) {
            setGameState({ currentScreen: 'auth' });
          } else {
            console.log('[App] Session error during resume, attempting to recover...');
            // Try to refresh the session instead of immediately dropping to auth
            try {
              const { data: refreshData, error: refreshError } = await authService.refreshSession();
              if (!refreshError && refreshData?.session?.user) {
                setSupabaseUser(refreshData.session.user);
                errorTrackingService.setUserId(refreshData.session.user.id);
                monitoringService.setUserId(refreshData.session.user.id);
                await handleUserSignedIn(refreshData.session.user, inviteCode);
                // Restore the saved state
                setGameState(() => ({
                  ...savedState,
                  isLoading: false,
                  isLoadingChat: false,
                  isListening: false,
                  error: null,
                }));
                return;
              }
            } catch (refreshError) {
              console.error('[App] Failed to refresh session:', refreshError);
            }
            // If refresh failed, then redirect to auth
            setGameState({ currentScreen: 'auth' });
          }
          return;
        }
        
        if (session?.user) {
          setSupabaseUser(session.user);
          errorTrackingService.setUserId(session.user.id);
          monitoringService.setUserId(session.user.id);
          await handleUserSignedIn(session.user, inviteCode);
          
          // Try to restore saved state after successful auth
          if (savedState && savedState.userProfile) {
            console.log('[App] Restoring saved game state');
            // DON'T override currentScreen - handleUserSignedIn already set it correctly
            const { currentScreen: _, ...stateToRestore } = savedState;
            setGameState(() => ({
              ...stateToRestore,
              // Keep the currentScreen that handleUserSignedIn set (lobby/userProfile/etc)
              isLoading: false,
              isLoadingChat: false,
              isListening: false,
              error: null,
            }));
          }
        } else {
          console.log('[App] No session found');
          // Check if we have a saved state that suggests we should have a session
          if (isResuming && lastScreen !== 'auth') {
            console.log('[App] Attempting to recover session from saved state...');
            // Give it one more try with a refresh
            try {
              const { data: refreshData } = await authService.refreshSession();
              if (refreshData?.session?.user) {
                setSupabaseUser(refreshData.session.user);
                await handleUserSignedIn(refreshData.session.user, inviteCode);
                setGameState(() => ({
                  ...savedState,
                  isLoading: false,
                  error: null,
                }));
                return;
              }
            } catch (e) {
              console.error('[App] Session recovery failed:', e);
            }
          }
          // Only show auth screen if we really have no session
          setGameState({ currentScreen: 'auth' });
        }
      } catch (error) {
        console.error('[App] Failed to initialize auth:', error);
        errorTrackingService.logError(error instanceof Error ? error : 'Authentication failed', {
          action: 'initialize_auth',
          screen: 'auth'
        });
        setGameState({
          currentScreen: 'auth',
          error: error instanceof Error ? error.message : 'Authentication failed',
        });
      } finally {
        setIsLoadingAuth(false);
      }
    };
    
    // Set up auth state change listener
    const { data: { subscription } } = authService.onAuthStateChange(async (event, session) => {
      console.log('[App] Auth state change event:', event, session?.user?.id);
      
      if (event === 'SIGNED_IN' && session?.user) {
        debounceAuthEvent('SIGNED_IN', session.user.id, async () => {
          setSupabaseUser(session.user);
          
          // Check if this is just a token refresh by looking at the last auth event
          const isTokenRefresh = lastAuthEvent?.type === 'TOKEN_REFRESHED' && 
                               lastAuthEvent?.userId === session.user.id &&
                               (Date.now() - lastAuthEvent.timestamp) < 2000; // Within 2 seconds
          
          // Only handle full sign-in flow if it's not just a token refresh
          if (!isTokenRefresh) {
            try {
              await handleUserSignedIn(session.user, inviteCode);
            } catch (error) {
              console.error('[App] Error during sign in handling:', error);
              setGameState({
                error: error instanceof Error ? error.message : 'Authentication error',
              });
            } finally {
              if (authTimeoutId) {
                clearTimeout(authTimeoutId);
                setAuthTimeoutId(null);
              }
            }
          } else {
            console.log('[App] Skipping sign-in handler for token refresh');
            // Clear auth loading states since we're not going through the full flow
            setIsHandlingAuth(false);
            setIsProfileLoadingState(false);
            if (authTimeoutId) {
              clearTimeout(authTimeoutId);
              setAuthTimeoutId(null);
            }
          }
        });
      } else if (event === 'SIGNED_OUT') {
        debounceAuthEvent('SIGNED_OUT', 'none', () => {
          setSupabaseUser(null);
          clearPersistedState();
          setGameState({
            currentScreen: 'auth',
            userProfile: null,
            currentRoom: null,
            isMultiplayer: false,
          });
          setIsLoadingAuth(false);
        });
      } else if (event === 'TOKEN_REFRESHED') {
        // Token refreshes don't need to trigger full auth flow
        console.log('[App] Token refreshed for user:', session?.user?.id);
        if (session?.user) {
          setSupabaseUser(session.user);
          // Only restore screen if we're currently on auth/lobby and have a valid saved screen
          const currentScreen = gameState.currentScreen;
          const lastScreen = localStorage.getItem('lucid_cyber_rpg_last_screen');
          
          if ((currentScreen === 'auth' || currentScreen === 'lobby') && lastScreen) {
      const validScreens = ['gameplay', 'graphical', 'main', 'cartridge_browser', 'mode_selection', 'room_browser', 'user_profile'];
            if (validScreens.includes(lastScreen)) {
              console.log('[App] Restoring screen after token refresh:', lastScreen);
              setGameState({ currentScreen: lastScreen as any });
              localStorage.removeItem('lucid_cyber_rpg_last_screen');
            }
          }
        }
      }
    });
    
    // Set a timeout to prevent infinite loading (only when page is visible and no user found)
    const authTimeout = setTimeout(() => {
      if (isLoadingAuth && !document.hidden && !supabaseUser) {
        console.warn('[App] Auth initialization timeout, showing auth screen');
        setIsLoadingAuth(false);
        setGameState({ currentScreen: 'auth' });
      }
    }, 10000); // Increased to 10 seconds
    
    initializeAuth().finally(() => {
      clearTimeout(authTimeout);
    });

    // Add combat navigation listener
    const handleNavigateToCombat = async () => {
      // Save game before entering combat
      if (gameState.currentScreen === 'gameplay') {
        console.log('[App] Saving game before entering combat...');
        await handleSaveGame();
      }
      
      // Create comprehensive pre-combat state
      const preCombatState = {
        currentPlayerState: gameState.currentPlayerState,
        selectedCartridge: gameState.selectedCartridge,
        currentRoom: gameState.currentRoom,
        storyHistory: gameState.storyHistory || [],
        selectedArchetype: gameState.selectedArchetype,
        currentStorySegment: gameState.currentStorySegment,
        currentSegmentIndex: gameState.currentSegmentIndex,
        timestamp: Date.now()
      };
      
      // Save pre-combat state to localStorage as backup
      localStorage.setItem('lucid_cyber_rpg_pre_combat_state', JSON.stringify(preCombatState));
      console.log('[App] Pre-combat state saved to localStorage');
      
      setGameState({
        currentScreen: 'combat',
        preCombatState,
      });
    };
    window.addEventListener('navigate-to-combat', handleNavigateToCombat);
    
    // Add trading modal listener
    const handleOpenTradingModal = () => {
      setShowTradingModal(true);
    };
    window.addEventListener('open-trading-modal', handleOpenTradingModal);

    // Add character chat listener
    const handleOpenCharacterChat = () => {
      setShowCharacterChat(true);
    };
    window.addEventListener('open-character-chat', handleOpenCharacterChat);
    
    // Add feed navigation listener
    const handleNavigateToFeed = () => {
      setGameState({ currentScreen: 'feed' as any });
    };
    window.addEventListener('navigate-to-feed', handleNavigateToFeed);

    // Session heartbeat to keep session alive
    const sessionHeartbeat = setInterval(async () => {
      try {
        const { data: { session } } = await supabaseClient.auth.getSession();
        if (session) {
          // This keeps the session active
          await supabaseClient.auth.getUser();
          console.log('[App] Session heartbeat - session active');
        }
      } catch (error) {
        console.error('[App] Heartbeat error:', error);
      }
    }, 4 * 60 * 1000); // Every 4 minutes

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('navigate-to-combat', handleNavigateToCombat);
      window.removeEventListener('open-trading-modal', handleOpenTradingModal);
      window.removeEventListener('open-character-chat', handleOpenCharacterChat);
      window.removeEventListener('navigate-to-feed', handleNavigateToFeed);
      window.removeEventListener('supabase-session-refreshed', handleSessionRefreshed);
      subscription.unsubscribe();
      clearInterval(sessionHeartbeat);
      
      // Cleanup timeouts to prevent memory leaks
      if (authTimeoutId) {
        clearTimeout(authTimeoutId);
      }
      if (saveTimeout) {
        clearTimeout(saveTimeout);
      }
    };
  }, []);

  const handleUserSignedIn = async (user: User, inviteCode?: string | null) => {
    if (isHandlingAuth) {
      console.log('[App] Auth handler already running, skipping');
      return;
    }
    
    console.log('[App] User signed in:', user.id);
    setIsHandlingAuth(true);
    setIsProfileLoadingState(true);
    setGameState({ error: null });
    
    try {
      // Attempt to get existing profile
      let profile = await databaseService.getUserProfile(user.id);
      
      // If no profile exists or profile is incomplete, create/update it
      if (!profile) {
        console.log('[App] No profile found, creating new profile');
        const newProfile = await databaseService.createUserProfile(
          user.id,
          user.email || '',
          user.user_metadata?.display_name || user.email?.split('@')[0] || 'Dreamer'
        );
        
        // Convert to UserProfile format
        profile = {
          uid: newProfile.id,
          email: newProfile.email,
          name: newProfile.display_name || newProfile.email?.split('@')[0] || 'Dreamer',
          displayName: newProfile.display_name || undefined,
          age: null,
          traits: [],
          mindset: null,
          appearanceImageUrl: undefined,
          stats: {
            health: { current: 100, max: 100 },
            spiritualEnergy: { current: 50, max: 50 },
            experience: 0,
            luck: 0,
            karma: 0
          },
          skills: [],
          inventory: [],
          creditsBalance: 100,
          subscriptionTier: 'free'
        };
      } else if (!profile.name || profile.name === '' || profile.displayName === null) {
        console.log('[App] Profile exists but incomplete, updating');
        // Update profile with proper name if missing
        const updatedName = profile.displayName || user.user_metadata?.display_name || user.email?.split('@')[0] || 'Dreamer';
        
        profile = {
          ...profile,
          name: updatedName,
          displayName: updatedName
        };
        
        // Save the updated profile
        await databaseService.updateUserProfile(user.id, {
          ...profile,
          displayName: updatedName
        });
      }
      
      console.log('[App] Profile ready:', profile);
      
      // Check if we need to handle an invite code
      if (inviteCode && profile) {
        console.log('[App] Attempting to join room with invite code:', inviteCode);
        const room = await databaseService.joinRoomByCode(inviteCode, profile);
        if (room) {
          setGameState({
            userProfile: profile,
            currentRoom: room,
            isMultiplayer: true,
            currentScreen: 'room_browser',
            isProfileLoading: false,
          });
          return;
        } else {
          console.warn('[App] Failed to join room with invite code');
        }
      }
      
      // Check if profile needs setup (missing traits, mindset, etc.)
      if (profile) {
        const needsSetup = !profile.traits || profile.traits.length === 0 || !profile.mindset;

        // Check saved state backup to see if we should restore to a specific screen
        const stateBackup = localStorage.getItem('lucid_cyber_rpg_state_backup');
        let savedScreen: string | null = null;
        if (stateBackup) {
          try {
            const backup = JSON.parse(stateBackup);
            savedScreen = backup.currentScreen;
          } catch (e) {}
        }

        // Check if we should preserve the current screen (to prevent unwanted lobby transitions)
        const currentScreen = gameState.currentScreen;
        const lastScreen = localStorage.getItem('lucid_cyber_rpg_last_screen');
        const importantScreens = ['gameplay', 'graphical', 'main', 'cartridge_browser', 'mode_selection', 'room_browser'];
        const isVisibilityRestore = importantScreens.includes(previousScreenRef.current) ||
                                   (lastScreen && importantScreens.includes(lastScreen)) ||
                                   (savedScreen && importantScreens.includes(savedScreen));
        const shouldPreserveScreen = currentScreen === 'gameplay' || currentScreen === 'main' ||
                                    currentScreen === 'cartridge_browser' || currentScreen === 'cartridge_editor' ||
                                    currentScreen === 'mode_selection' || currentScreen === 'room_browser' ||
                                    currentScreen === 'userProfile' ||
                                    isVisibilityRestore;

        // Determine the target screen
        let targetScreen: GameState['currentScreen'];
        if (needsSetup) {
          targetScreen = 'userProfile';
        } else if (savedScreen && importantScreens.includes(savedScreen)) {
          // PRIORITY: Use saved screen from backup if it's an important screen
          targetScreen = savedScreen as GameState['currentScreen'];
          console.log(`[App] Restoring to saved screen: ${savedScreen}`);
        } else if (shouldPreserveScreen && isVisibilityRestore && lastScreen) {
          targetScreen = lastScreen as GameState['currentScreen'];
        } else if (shouldPreserveScreen) {
          targetScreen = currentScreen;
        } else {
          targetScreen = 'lobby';
        }
        
        console.log('[App] Profile setup check:', { 
          needsSetup, 
          traits: profile.traits, 
          mindset: profile.mindset?.name || 'null',
          currentScreen,
          shouldPreserveScreen,
          targetScreen
        });
        
        setGameState({
          userProfile: profile,
          currentScreen: targetScreen,
          isProfileLoading: false,
        });
        
        console.log('[App] Screen transition completed to:', targetScreen);
        
        // Check if there's a pending room to join
        if (pendingRoomId && !needsSetup) {
          console.log('[App] Joining pending room:', pendingRoomId);
          handleJoinRoom(pendingRoomId);
          setPendingRoomId(null);
        }
      }
      
    } catch (error) {
      console.error('[App] Error handling signed in user:', error);
      setGameState({
        error: `Profile error: ${error instanceof Error ? error.message : 'Unknown error'}`,
        isProfileLoading: false,
      });
    } finally {
      setIsProfileLoadingState(false);
      setIsHandlingAuth(false);
    }
  };

  const handleSetError = useCallback((error: string | null) => {
    if (error) {
      errorTrackingService.logError(error, { 
        screen: gameState.currentScreen,
        action: 'handle_set_error'
      });
    }
    setGameState({ error });
  }, [gameState.currentScreen]);

  const handleSetLoadingAppearance = useCallback((isLoading: boolean) => {
    setGameState({ isLoadingAppearance: isLoading });
  }, []);

  const handleUpdateProfileImage = useCallback(async (updates: Partial<UserProfile>) => {
    if (gameState.userProfile && updates.hasOwnProperty('appearanceImageUrl')) {
      const userId = supabaseUser?.id || gameState.userProfile.uid;
      
      if (!userId) {
        console.error('[App] Cannot update profile: No user ID found');
        setGameState({ error: 'Cannot save profile: User not authenticated' });
        return;
      }
      
      try {
        console.log('[App] Updating user profile image for user:', userId);
        console.log('[App] Image URL:', updates.appearanceImageUrl);
        
        // Update the profile in Supabase first
        await databaseService.updateUserProfile(userId, updates);
        
        // Only update local state if Supabase update was successful
        const updatedProfile = { ...gameState.userProfile, appearanceImageUrl: updates.appearanceImageUrl };
        setGameState({ userProfile: updatedProfile });
        
        console.log('[App] Profile image updated successfully');
      } catch (error) {
        console.error('[App] Failed to update profile image:', error);
        setGameState({
          error: `Failed to save profile image: ${error instanceof Error ? error.message : 'Unknown error'}`,
        });
      }
    }
  }, [gameState.userProfile, supabaseUser?.id]);

  const constructInitialNarrative = (userProfile: UserProfile | null): string => {
    if (!userProfile) return INITIAL_NARRATIVE_PROMPT_BASE;
    return fillUserProfilePlaceholders(INITIAL_NARRATIVE_PROMPT_BASE, userProfile);
  };

  const processStoryDataToChatMessages = (storyData: any, apiKey?: string): ChatMessage[] => {
    const newMessages: ChatMessage[] = [];
    const processedMessages = new Set<string>(); // Track messages to avoid duplicates
    const timestamp = Date.now();
    
    // Create deduplication manager for this processing
    const deduplicationManager = new MessageDeduplicationManager();
    
    // Helper function to add message with deduplication
    const addMessage = (companionMsg: CompanionMessage) => {
      // Handle different property names for the message text
      const messageContent = (companionMsg.dialogue || companionMsg.message || companionMsg.text || '').trim();
      if (!messageContent) {
        console.warn('[App] Companion message has no text content:', companionMsg);
        return;
      }
      const messageKey = `${companionMsg.name || companionMsg.speaker || 'Unknown'}:${messageContent}`;
      
      // Check with deduplication manager
      const isDuplicate = deduplicationManager.checkAndTrack(messageKey);
      
      // Check if we already processed this exact message
      if (!isDuplicate && !processedMessages.has(messageKey)) {
        processedMessages.add(messageKey);
        newMessages.push({
          id: uuidv4(),
          text: messageContent, // Use the extracted content
          sender: companionMsg.name || companionMsg.speaker || 'Unknown', // Handle different property names
          timestamp: timestamp + newMessages.length // Slightly stagger timestamps
        });
      } else {
        console.log(`[App] Skipping duplicate companion message in story data from ${companionMsg.name || companionMsg.speaker}:`, messageContent);
      }
    };
    
    // Handle companion messages from story segment
    if (storyData.companionMessages && Array.isArray(storyData.companionMessages)) {
      console.log('[App] Processing companionMessages:', storyData.companionMessages);
      storyData.companionMessages.forEach(addMessage);
    }
    
    // Handle companion_dialogue from OpenRouter response format  
    if (storyData.companion_dialogue && Array.isArray(storyData.companion_dialogue)) {
      console.log('[App] Processing companion_dialogue:', storyData.companion_dialogue);
      storyData.companion_dialogue.forEach(addMessage);
    }

    console.log(`[App] Processed ${newMessages.length} companion messages from story data`);
    return newMessages;
  };

  const handleProfileComplete = (profile: UserProfile) => {
    setGameState({ userProfile: profile, currentScreen: 'lobby' });
    databaseService.saveUserProfile(profile);
  };

  const handleSelectCartridge = async (cartridge: GameCartridge) => {
    console.log('[App] handleSelectCartridge called with:', cartridge);
    
    // Check if user has any previous progress with this cartridge
    if (supabaseUser && cartridge.id) {
      try {
        // Get all player branches for this cartridge
        const { data: branches, error } = await supabaseClient
          .from('narrative_branches')
          .select('*')
          .eq('cartridge_id', cartridge.id)
          .like('branch_name', `cartridge/${cartridge.id}/%/${supabaseUser.id}`)
          .order('created_at', { ascending: false });
        
        if (!error && branches && branches.length > 0) {
          // User has played this cartridge before
          console.log('[App] Found existing progress branches:', branches.length);
          
          // Extract instance IDs from branch names
          const instancesWithProgress = branches.map(branch => {
            const parts = branch.branch_name.split('/');
            return {
              instanceId: parts[2],
              branchName: branch.branch_name,
              lastActivity: branch.updated_at,
              commitCount: branch.commit_count || 0
            };
          });
          
          // TODO: Show UI to let user choose to continue or start new
          // For now, we'll just log this info and proceed normally
          console.log('[App] Player has progress in instances:', instancesWithProgress);
          
          // Store this info in the cartridge metadata for later use
          cartridge.playerProgress = instancesWithProgress;
        }
      } catch (error) {
        console.error('[App] Error checking for previous progress:', error);
        // Continue normally if error occurs
      }
    }
    
    setGameState((state) => {
      console.log('[App] Previous state:', state.currentScreen);
      const newState = {
        selectedCartridge: cartridge,
        currentScreen: 'mode_selection' as const,
      };
      console.log('[App] New state will be:', newState.currentScreen);
      return newState;
    });
  };

  const handleSelectSinglePlayer = () => {
    // Check if we have any progress to restore
    const cartridge = gameState.selectedCartridge;
    if (cartridge?.playerProgress && cartridge.playerProgress.length > 0) {
      // Show continue dialog
      setGameState({
        isMultiplayer: false,
        currentScreen: 'continue_dialog',
        currentRoom: null,
      });
    } else {
      // No progress, go directly to main screen
      setGameState({
        isMultiplayer: false,
        currentScreen: 'main',
        currentRoom: null,
      });
    }
  };

  const handleSelectMultiplayer = () => {
    setGameState({ currentScreen: 'room_browser' });
  };

  const handleSelectGraphicalPreview = useCallback(() => {
    setGameState({
      currentScreen: 'graphical',
      error: null,
    });
  }, [setGameState]);

  const handleExitGraphicalPreview = useCallback(() => {
    setGameState({
      currentScreen: 'mode_selection',
    });
  }, [setGameState]);

  const handleContinueGame = async (instanceId: string) => {
    if (!supabaseUser || !gameState.selectedCartridge) {
      console.error('[App] Cannot continue game: missing user or cartridge');
      return;
    }

    setGameState({ isLoading: true, error: null });
    
    try {
      // Load the latest progress for this instance
      const progress = await playerProgressService.getLatestProgress(
        supabaseUser.id,
        gameState.selectedCartridge.id,
        instanceId
      );

      if (!progress) {
        throw new Error('No progress found for this instance');
      }

      console.log('[App] Restored progress:', progress);

      // Restore the game state from the saved progress
      const restoredState = progress.gameState || progress;
      
      // Extract the necessary data from restored state
      const playerState: PlayerState = {
        userProfile: gameState.userProfile!,
        archetype: restoredState.archetype || restoredState.playerState?.archetype,
        location: restoredState.metadata?.location || restoredState.location || 'unknown location',
        status: 'active',
        storyHistory: restoredState.storyHistory || [],
        currentStorySegment: restoredState.currentStorySegment
      };

      const storySegment: StorySegment = restoredState.currentStorySegment || {
        narrative: restoredState.narrative || "Resuming your journey...",
        choices: restoredState.choices || ["Continue", "Look around", "Rest"],
        companionMessages: restoredState.companionMessages || [],
        imagePrompt: restoredState.imagePrompt || "cyberpunk scene continuation"
      };

      // Process chat messages if available
      const chatMessages = restoredState.chatMessages || 
        (storySegment.narrative ? processStoryDataToChatMessages({
          narrative: storySegment.narrative,
          companion_dialogue: storySegment.companionMessages || [],
          choices: storySegment.choices,
          image_prompt: storySegment.imagePrompt || ""
        }) : []);

      // Store the instance ID for future saves
      const sessionId = `${gameState.selectedCartridge.id}_${instanceId}_${Date.now()}`;

      setGameState({
        currentPlayerState: playerState,
        currentStorySegment: storySegment,
        chatMessages,
        currentSessionId: sessionId,
        currentScreen: 'gameplay',
        isLoading: false,
        // Store the instance ID for progress tracking
        currentInstanceId: instanceId,
      });

      // Track the continue action
      track('game_continued', {
        cartridgeId: gameState.selectedCartridge.id,
        instanceId: instanceId
      });

    } catch (error) {
      console.error('[App] Error continuing game:', error);
      setGameState({
        isLoading: false,
        error: 'Failed to load your saved progress. Please try starting a new game.',
        currentScreen: 'main',
      });
    }
  };

  const handleNewGameFromDialog = () => {
    // Simply go to main screen to start fresh
    setGameState({
      currentScreen: 'main',
      // Clear any instance ID from previous sessions
      currentInstanceId: undefined,
    });
  };

  const handleCancelContinueDialog = () => {
    // Go back to mode selection
    setGameState({ currentScreen: 'mode_selection' });
  };

  const handleBackToModeSelection = () => {
    setGameState({
      currentScreen: 'mode_selection',
      currentRoom: null,
      isMultiplayer: false,
    });
  };

  const handleBackToCartridges = () => {
    setGameState({ currentScreen: 'cartridge_browser', selectedCartridge: null });
  };

  const handleCreateCartridge = () => {
    setGameState({ currentScreen: 'cartridge_editor' });
  };

  const handleCreateGame = () => {
    // Navigate to cartridge selection for creating a new game
    setGameState({ currentScreen: 'cartridge_browser' });
  };

  const handleJoinGame = async (gameId: string) => {
    if (!gameState.userProfile) return;
    
    try {
      console.log('[App] Rejoining game session:', gameId);
      
      // Load the saved game state for this session
      const savedGameData = await databaseService.loadGameState(gameId);
      
      if (savedGameData && savedGameData.currentStorySegment && savedGameData.currentPlayerState) {
        // User has existing game progress, go directly to gameplay
        console.log('[App] Found existing game progress, resuming gameplay');
        setGameState({
          currentScreen: 'gameplay',
          currentSessionId: gameId,
          currentStorySegment: savedGameData.currentStorySegment,
          currentPlayerState: savedGameData.currentPlayerState,
          selectedCartridge: savedGameData.selectedCartridge || null,
          chatMessages: savedGameData.chatMessages || [],
        });
      } else {
        // No saved progress, user needs to select archetype
        console.log('[App] No existing progress found, going to character selection');
        setGameState({
          currentScreen: 'main',
          currentSessionId: gameId,
        });
      }
    } catch (error) {
      console.error('Error rejoining game:', error);
      setGameState({ error: 'Failed to rejoin game' });
    }
  };

  const handleCreateRoom = async (params: CreateRoomParameters) => {
    if (!gameState.userProfile || !gameState.selectedCartridge) {
      setGameState({ error: 'Missing user profile or cartridge selection' });
      return;
    }
    
    setGameState({ isLoading: true, error: null });
    
    try {
      console.log('[App] Creating multiplayer room with params:', params);
      
      // Create room using the multiplayer service
      const { roomId, shareableLink } = await multiplayerStoryService.createRoom(
        gameState.selectedCartridge.id,
        gameState.userProfile,
        {
          isPublic: params.isPublic,
          maxPlayers: params.maxPlayers,
          roomName: params.name
        }
      );
      
      console.log('[App] Room created successfully:', { roomId, shareableLink });
      
      // Create room object for state
      const newRoom: MultiplayerRoom = {
        id: roomId,
        name: params.name,
        description: params.description,
        hostId: gameState.userProfile.uid || '',
        currentPlayers: [gameState.userProfile.uid || ''],
        maxPlayers: params.maxPlayers,
        isPublic: params.isPublic,
        gameState: {},
        cartridgeId: gameState.selectedCartridge.id,
        createdAt: new Date(),
        updatedAt: new Date()
      };
      
      // Update state with new room and navigate to main screen
      setGameState({
        currentRoom: newRoom,
        isMultiplayer: true,
        currentScreen: 'main',
        isLoading: false,
      });
      
    } catch (error) {
      console.error('[App] Error creating room:', error);
      setGameState({
        error: `Failed to create room: ${error instanceof Error ? error.message : 'Unknown error'}`,
        isLoading: false,
      });
    }
  };

  const handleJoinRoom = async (roomToJoin: MultiplayerRoom) => {
    if (!gameState.userProfile) {
      setGameState({ error: 'User profile required to join room' });
      return;
    }
    
    setGameState({ isLoading: true, error: null });
    
    try {
      console.log('[App] Joining multiplayer room:', roomToJoin.id);
      
      // Ensure currentPlayers in roomToJoin are valid UIDs before using the room object
      const validPlayerUIDs = roomToJoin.currentPlayers.filter(uid => typeof uid === 'string' && uid.trim() !== '');
      const cleanedRoomToJoin = { ...roomToJoin, currentPlayers: validPlayerUIDs };

      // Join room using the multiplayer service
      const userId = gameState.userProfile?.uid || supabaseUser?.id || '';
      const success = await multiplayerStoryService.joinRoom(cleanedRoomToJoin.id, userId);
      
      if (success) {
        console.log('[App] Successfully joined room');
        
        // Update state with joined room and navigate to main screen
        setGameState({
          currentRoom: cleanedRoomToJoin, // Use the cleaned room object
          isMultiplayer: true,
          currentScreen: 'main',
          isLoading: false,
        });
      } else {
        throw new Error('Failed to join room');
      }
      
    } catch (error) {
      console.error('[App] Error joining room:', error);
      setGameState({
        error: `Failed to join room: ${error instanceof Error ? error.message : 'Unknown error'}`,
        isLoading: false,
      });
    }
  };

  const handleSignOut = async () => {
    try {
      await authService.signOut();
      setGameState({
        currentScreen: 'auth',
        userProfile: null,
        currentPlayerState: null,
        selectedCartridge: null,
        currentRoom: null,
        currentSessionId: null,
        chatMessages: [],
        isMultiplayer: false,
      });
    } catch (error) {
      console.error('Error signing out:', error);
      setGameState({ error: 'Failed to sign out' });
    }
  };

  // Placeholder handlers for GameplayScreen
  const handleJoinAdventure = async (archetype: Archetype) => {
    if (!gameState.userProfile || !gameState.selectedCartridge) {
      console.error('[App] Cannot start game without profile or cartridge');
      return;
    }

    console.log('[App] Starting adventure with archetype:', archetype.name);
    setGameState({ isLoading: true, error: null });
    
    let retries = 3;
    let lastError: Error | null = null;
    
    while (retries > 0) {
      try {
        // Initialize player state
        const initialPlayerState: PlayerState = {
          userProfile: gameState.userProfile,
          archetype: archetype,
          location: gameState.selectedCartridge?.worldSettings?.initialLocation || 'Neo-Veridia Central District',
          status: 'active',
          storyHistory: [],
          currentStorySegment: null
        };

        setGameState({ currentPlayerState: initialPlayerState });

        // Generate initial story segment
        const initialNarrative = constructInitialNarrative(gameState.userProfile);
        const storyData = await generateStorySegment(
          initialNarrative, 
          "Begin the adventure", 
          "The journey starts now.", 
          archetype.description, 
          gameState.userProfile, 
          supabaseUser?.id
        );

        if (!storyData || !storyData.choices || storyData.choices.length === 0) {
          throw new Error('Failed to generate valid story data');
        }

        const newSegment: StorySegment = {
          id: uuidv4(),
          narrative: storyData.narrative,
          imagePrompt: (storyData.image_prompt && storyData.image_prompt.trim())
            ? storyData.image_prompt
            : 'cyberpunk megacity street scene at night, neon rain, holographic ads, cinematic lighting, highly detailed',
          choices: storyData.choices,
          companionMessages: storyData.companion_dialogue || [],
          imageUrl: storyData.imageUrl || null
        };

        // Process companion messages
        const companionMessages = processStoryDataToChatMessages(storyData);
        
        console.log('[App] Game initialized successfully');
        setGameState({
          currentScreen: 'gameplay',
          currentStorySegment: newSegment,
          chatMessages: companionMessages,
          isLoading: false,
          error: null,
        });
        
        return; // Success, exit the function
        
      } catch (error) {
        lastError = error instanceof Error ? error : new Error('Unknown error');
        console.error(`[App] Game initialization attempt ${4 - retries} failed:`, error);
        retries--;
        
        if (retries > 0) {
          console.log(`[App] Retrying... (${retries} attempts left)`);
          await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second before retry
        }
      }
    }
    
    // All retries failed
    console.error('[App] Failed to start game after all retries');
    setGameState({
      isLoading: false,
      error: `Failed to start adventure: ${lastError?.message || 'Unknown error'}. Please try again.`,
    });
  };

  const handleMakeChoice = async (choiceText: string) => {
    console.log("Choice selected:", choiceText);
    
    if (!gameState.userProfile || !gameState.currentStorySegment) {
      console.error("Cannot make choice: missing user profile or story segment");
      return;
    }

    setGameState({ isLoading: true, error: null });
    
    try {
      // 1. Build game history summary for context
      const gameHistorySummary = gameState.currentPlayerState?.storyHistory
        ?.map(segment => segment.narrative)
        ?.slice(-3) // Last 3 segments for context
        ?.join('\n') || 'Adventure in progress.';

      // 1.5. Moderate the choice before processing
      const moderatedChoice = await contentModerationService.validatePlayerChoice(
        choiceText,
        supabaseUser?.id
      );
      console.log("[App] Moderated choice:", moderatedChoice);
      
      // 2. Generate new story segment based on moderated choice
      const storyResponse = await errorTrackingService.measureAsync('story_generation', async () => {
        return await generateStorySegment(
          gameState.currentStorySegment!.narrative,
          moderatedChoice,
          gameHistorySummary,
          gameState.currentPlayerState?.archetype?.description || '',
          gameState.userProfile,
          supabaseUser?.id
        );
      });

      // 3. Use image from story response or generate if not available
      let sceneImageUrl: string | null = storyResponse.imageUrl || null;
      if (!sceneImageUrl && storyResponse.image_prompt) {
        try {
          console.log('[App] Generating scene image for:', storyResponse.image_prompt);
          sceneImageUrl = await generateImage(
            storyResponse.image_prompt,
            supabaseUser?.id,
            'scene'
          );
          console.log('[App] Scene image generated successfully:', sceneImageUrl);
        } catch (error) {
          console.warn('[App] Failed to generate scene image:', error);
          // Continue without image - don't block the story
        }
      } else if (sceneImageUrl) {
        console.log('[App] Using image from story response:', sceneImageUrl);
      }

      // 4. Create new story segment with unique ID
      const newStorySegment: StorySegment = {
        id: `segment_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        narrative: storyResponse.narrative,
        imagePrompt: (storyResponse.image_prompt && storyResponse.image_prompt.trim())
          ? storyResponse.image_prompt
          : 'cyberpunk alley with neon reflections, rain, holograms, cinematic lighting, highly detailed',
        choices: storyResponse.choices,
        companionMessages: storyResponse.companion_dialogue,
        imageUrl: sceneImageUrl
      };

      // 5. Process companion dialogue into chat messages
      console.log('[App] Story response companion_dialogue:', storyResponse.companion_dialogue);
      const newChatMessages = processStoryDataToChatMessages(storyResponse);
      console.log('[App] Processed chat messages:', newChatMessages);

      // Force companion dialogue if none was generated
      if (newChatMessages.length === 0 || !newChatMessages.some(msg => msg.sender === 'JAX' || msg.sender === 'Lyra')) {
        console.log('[App] No companion dialogue generated, adding forced companions');
        const forcedCompanions = [
          {
            id: uuidv4(),
            text: "Neural pathways activated. Analyzing your choice...",
            sender: 'JAX',
            timestamp: Date.now()
          },
          {
            id: uuidv4(), 
            text: "The dream responds to your decision. I sense new possibilities.",
            sender: 'Lyra',
            timestamp: Date.now() + 1
          }
        ];
        newChatMessages.push(...forcedCompanions);
      }
      
      // 6. Apply stat changes if provided
      let updatedProfile = gameState.userProfile;
      if (storyResponse.stat_changes) {
        updatedProfile = {
          ...updatedProfile,
          stats: {
            ...updatedProfile.stats,
            health: {
              current: Math.max(0, Math.min(
                updatedProfile.stats.health.max,
                updatedProfile.stats.health.current + (storyResponse.stat_changes.health || 0)
              )),
              max: updatedProfile.stats.health.max
            },
            spiritualEnergy: {
              current: Math.max(0, Math.min(
                updatedProfile.stats.spiritualEnergy.max,
                updatedProfile.stats.spiritualEnergy.current + (storyResponse.stat_changes.spiritualEnergy || 0)
              )),
              max: updatedProfile.stats.spiritualEnergy.max
            },
            experience: updatedProfile.stats.experience + (storyResponse.stat_changes.experience || 0),
            luck: updatedProfile.stats.luck,
            karma: updatedProfile.stats.karma
          }
        };
        
        // Save updated profile to Supabase
        await databaseService.saveUserProfile(updatedProfile);
      }

      // 7. Update game state
      setGameState((state) => ({
        currentStorySegment: newStorySegment,
        userProfile: updatedProfile,
        chatMessages: [...state.chatMessages, ...newChatMessages],
        currentPlayerState: state.currentPlayerState
          ? {
              ...state.currentPlayerState,
              storyHistory: [
                ...state.currentPlayerState.storyHistory,
                state.currentStorySegment!,
              ],
              currentStorySegment: newStorySegment,
              userProfile: updatedProfile,
            }
          : null,
        isLoading: false,
      }));
      
      console.log(`[App] Choice processed successfully. Added ${newChatMessages.length} companion messages.`);
      
      // Track game event
      errorTrackingService.logGameEvent('choice_made', {
        choice: choiceText,
        companionMessages: newChatMessages.length,
        storySegmentId: newStorySegment.id,
        hasImage: !!sceneImageUrl,
        statChanges: !!storyResponse.stat_changes
      });

      // Save progress to player's git-like branch
      try {
        if (gameState.userProfile && gameState.selectedCartridge) {
          // Use existing instanceId if continuing a game, otherwise generate new one
          const instanceId = gameState.currentInstanceId || gameState.currentSessionId || `instance-${Date.now()}`;
          
          // Store instanceId for future saves if it's new
          if (!gameState.currentInstanceId) {
            setGameState({ currentInstanceId: instanceId });
          }
          
          let playerBranch = await playerProgressService.getPlayerBranch(
            gameState.userProfile.uid || supabaseUser?.id || 'anonymous',
            gameState.selectedCartridge.id,
            instanceId
          );

          if (!playerBranch) {
            playerBranch = await playerProgressService.createPlayerBranch(
              gameState.userProfile.uid || supabaseUser?.id || 'anonymous',
              gameState.selectedCartridge.id,
              gameState.selectedCartridge.name
            );
          }

          await playerProgressService.commitProgress(
            playerBranch.branchName,
            [choiceText],
            newStorySegment.id || 'unknown',
            {
              currentStorySegment: newStorySegment,
              chatMessages: [...gameState.chatMessages, ...newChatMessages],
              userProfile: updatedProfile,
              selectedCartridge: gameState.selectedCartridge
            },
            {
              karma: updatedProfile.stats.karma,
              experience: updatedProfile.stats.experience,
              health: updatedProfile.stats.health.current,
              spiritualEnergy: updatedProfile.stats.spiritualEnergy.current,
              location: gameState.currentPlayerState?.location || 'Unknown'
            }
          );

          console.log('[App] Progress committed to player branch:', playerBranch.branchName);

          // Track decision in decision tree
          // For now, we'll use the current session ID as a placeholder
          // In a full implementation, we'd track player_game_sessions properly
          const sessionId = gameState.currentSessionId || `session_${Date.now()}`;
          
          // TODO: Implement proper player_game_sessions tracking
          // This is a placeholder implementation for the decision tree
          console.log('[App] Decision tree tracking temporarily disabled - needs player_game_sessions implementation');
        }
      } catch (progressError) {
        console.warn('[App] Failed to save progress to player branch:', progressError);
        // Don't block the game if progress saving fails
      }
      
    } catch (error) {
      console.error('Error processing choice:', error);
      
      // Enhanced error logging with more context
      const errorDetails = {
        action: 'make_choice',
        screen: 'gameplay',
        choice: choiceText,
        errorType: error instanceof Error ? error.constructor.name : 'Unknown',
        errorMessage: error instanceof Error ? error.message : String(error),
        gameState: {
          hasStorySegment: !!gameState.currentStorySegment,
          hasPlayerState: !!gameState.currentPlayerState,
          hasUserProfile: !!gameState.userProfile,
          selectedCartridge: gameState.selectedCartridge?.name || 'None',
          isMultiplayer: gameState.isMultiplayer,
          sessionId: gameState.currentSessionId
        }
      };
      
      errorTrackingService.logError(error instanceof Error ? error : 'Failed to process choice', errorDetails);
      
      // Provide specific error messages based on error type
      let userMessage = 'Failed to process choice';
      if (error instanceof Error) {
        if (error.message.includes('schema') || error.message.includes('column')) {
          userMessage = 'Database configuration issue detected. Please contact support.';
        } else if (error.message.includes('network') || error.message.includes('fetch')) {
          userMessage = 'Network connection issue. Please check your internet and try again.';
        } else if (error.message.includes('auth') || error.message.includes('permission')) {
          userMessage = 'Authentication issue. Please try logging out and back in.';
        } else {
          userMessage = `Choice processing failed: ${error.message}`;
        }
      }
      
      // Create emergency fallback story segment to keep the game playable
      const emergencyStorySegment: StorySegment = {
        id: `emergency_${Date.now()}`,
        narrative: `The neural connection wavers momentarily as digital static interferes with your consciousness stream. The chrome-plated walls of Neo-Veridia flicker with corrupted data cascades, but your augmented reality systems quickly compensate. In the distance, the pulsing neon of the financial district casts an ethereal glow through the perpetual smog. Your next move becomes critical as the systems struggle to stabilize.`,
        imagePrompt: 'cyberpunk scene with digital glitches, neon lights, chrome architecture',
        choices: [
          'Attempt to re-establish neural connection',
          'Navigate using backup sensory protocols',
          'Seek technical assistance from nearby contacts'
        ],
        companionMessages: [
          { name: "JAX", dialogue: "System integrity compromised but operational. Running diagnostics now." },
          { name: "Lyra", dialogue: "I sense disturbance in the digital flow. Let's proceed with caution." }
        ],
        imageUrl: null
      };

      setGameState((state) => ({
        isLoading: false,
        error: `${userMessage}. The story continues with backup systems.`,
        currentStorySegment: emergencyStorySegment,
        chatMessages: [
          ...state.chatMessages,
          {
            id: uuidv4(),
            text: 'System Alert: Neural interface experiencing temporary disruption. Backup narrative protocols engaged.',
            sender: 'System',
            timestamp: Date.now(),
            type: 'system',
          },
        ],
      }));
    }
  };

  const handleSendChatMessage = async (messageText: string, senderName?: string) => {
    console.log("[App] *** CHAT MESSAGE SENT ***:", messageText);
    console.log("[App] Chat sender:", senderName);
    
    if (!gameState.userProfile) {
      console.error("[App] Cannot send chat message: missing user profile");
      return;
    }

    const playerName = senderName || gameState.userProfile.name;
    console.log("[App] Using player name:", playerName);
    
    // Check for duplicate messages in the last 2 seconds to prevent React StrictMode and other duplicates
    const now = Date.now();
    const recentMessages = gameState.chatMessages.filter(msg => 
      now - msg.timestamp < 2000 && // Within last 2 seconds
      msg.text.trim() === messageText.trim() && // Same text content
      msg.sender === playerName // Same sender
    );
    
    if (recentMessages.length > 0) {
      console.log("[App] Duplicate message detected, ignoring:", messageText);
      return; // Ignore duplicate message
    }
    
    // Moderate the message before adding
    const moderatedMessage = await contentModerationService.sanitizeChatMessage(
      messageText, 
      user?.id
    );
    console.log("[App] Moderated message:", moderatedMessage);
    
    // Add user's message immediately with moderated content
    const userMessage: ChatMessage = {
      id: uuidv4(),
      text: moderatedMessage,
      sender: playerName,
      timestamp: now
    };

    setGameState((state) => ({
      chatMessages: [...state.chatMessages, userMessage],
      isLoadingChat: true,
    }));

    try {
      console.log("[App] Calling OpenRouter generateChatResponse with:", {
        messageText,
        chatHistoryLength: gameState.chatMessages.slice(-10).length,
        hasStorySegment: !!gameState.currentStorySegment,
        archetype: gameState.currentPlayerState?.archetype?.name,
        userId: supabaseUser?.id
      });
      
      // Generate companion response using chat service
      const chatResponse = await generateChatResponse(
        messageText,
        gameState.chatMessages.slice(-10), // Last 10 messages for context
        gameState.currentStorySegment || {
          narrative: 'The adventure continues...',
          imagePrompt: '',
          choices: [],
          companionMessages: []
        }, // currentStorySegment parameter
        gameState.currentPlayerState?.archetype || {
          id: 'default',
          name: 'Wanderer',
          description: 'A wandering soul in the digital realm',
          initialPromptSuffix: ''
        }, // archetype parameter
        gameState.userProfile,
        supabaseUser?.id
      );
      
      console.log("[App] OpenRouter chat response received:", {
        hasDialogues: !!chatResponse.dialogues,
        dialoguesCount: chatResponse.dialogues?.length || 0,
        dialogues: chatResponse.dialogues
      });

      // Process companion responses with deduplication
      const companionMessages: ChatMessage[] = [];
      const responseTimestamp = Date.now();
      const processedMessages = new Set<string>(); // Track messages we're about to add
      
      if (chatResponse.dialogues) {
        chatResponse.dialogues.forEach((dialogue: CompanionMessage) => {
          const messageContent = dialogue.dialogue.trim();
          const messageKey = `${dialogue.name}:${messageContent}`;
          
          // Create a simple deduplication manager inline
          const deduplicationManager = new MessageDeduplicationManager();
          
          // Check if this is a duplicate message
          const isDuplicate = deduplicationManager.checkAndTrack(messageKey);
          
          // Also check for duplicate companion messages in the last 5 seconds
          const recentCompanionMessages = gameState.chatMessages.filter(msg => 
            responseTimestamp - msg.timestamp < 5000 && // Within last 5 seconds
            msg.text.trim() === messageContent && // Same text content
            msg.sender === dialogue.name // Same companion
          );
          
          // Only add if not a duplicate by any check
          if (!isDuplicate && recentCompanionMessages.length === 0 && !processedMessages.has(messageKey)) {
            processedMessages.add(messageKey);
            companionMessages.push({
              id: uuidv4(),
              text: dialogue.dialogue,
              sender: dialogue.name,
              timestamp: responseTimestamp + companionMessages.length // Slightly stagger timestamps
            });
          } else {
            console.log(`[App] Duplicate companion message from ${dialogue.name} detected and filtered, skipping:`, dialogue.dialogue);
          }
        });
      }

      // Apply any stat changes from companion interactions
      let updatedProfile = gameState.userProfile;
      if (chatResponse.stat_changes) {
        updatedProfile = {
          ...updatedProfile,
          stats: {
            ...updatedProfile.stats,
            health: {
              current: Math.max(0, Math.min(
                updatedProfile.stats.health.max,
                updatedProfile.stats.health.current + (chatResponse.stat_changes.health || 0)
              )),
              max: updatedProfile.stats.health.max
            },
            spiritualEnergy: {
              current: Math.max(0, Math.min(
                updatedProfile.stats.spiritualEnergy.max,
                updatedProfile.stats.spiritualEnergy.current + (chatResponse.stat_changes.spiritualEnergy || 0)
              )),
              max: updatedProfile.stats.spiritualEnergy.max
            },
            experience: updatedProfile.stats.experience + (chatResponse.stat_changes.experience || 0),
            luck: updatedProfile.stats.luck,
            karma: updatedProfile.stats.karma
          }
        };
        
        // Save updated profile to Supabase
        await databaseService.saveUserProfile(updatedProfile);
      }

      setGameState((state) => ({
        chatMessages: [...state.chatMessages, ...companionMessages],
        userProfile: updatedProfile,
        isLoadingChat: false,
      }));

      console.log(`[App] Chat processed successfully. Added ${companionMessages.length} companion responses.`);

    } catch (error) {
      console.error('Error processing chat message:', error);
      
      // Add fallback companion response on error
      const fallbackMessage: ChatMessage = {
        id: uuidv4(),
        text: "The neural link flickers... communication temporarily disrupted.",
        sender: 'JAX',
        timestamp: Date.now()
      };

      setGameState((state) => ({
        chatMessages: [...state.chatMessages, fallbackMessage],
        isLoadingChat: false,
        error: `Chat error: ${error instanceof Error ? error.message : 'Unknown error'}`,
      }));
    }
  };

  const handleToggleListening = () => {
    if (!speechService.isSupported()) {
      setGameState({
        microphoneError:
          'Speech recognition not supported in this browser. Please use Chrome, Edge, or Safari.',
      });
      return;
    }

    if (gameState.isListening) {
      speechService.stopListening();
      setGameState({ isListening: false, microphoneError: null });
    } else {
      // Clear any previous errors
      setGameState({ isListening: true, microphoneError: null });
      
      speechService.startListening(
        (transcript: string) => {
          console.log('[App] Speech recognized:', transcript);
          // Automatically send the transcript as a chat message
          handleSendChatMessage(transcript, gameState.userProfile?.name || 'Player');
          setGameState({ isListening: false, microphoneError: null });
        },
        (error: string) => {
          console.error('[App] Speech recognition error:', error);
          
          // Set specific microphone error message
          let microphoneError = error;
          if (error.includes('not-allowed') || error.includes('permission')) {
            microphoneError = "Microphone access denied. Please allow microphone permissions in your browser settings.";
          } else if (error.includes('no-speech')) {
            microphoneError = "No speech detected. Please try speaking again.";
          } else if (error.includes('network')) {
            microphoneError = "Network error. Using offline mode with mock responses.";
          } else if (error.includes('not available') || error.includes('not supported')) {
            microphoneError = "Speech recognition not available. Please use Chrome, Edge, or Safari.";
          }
          
          setGameState({
            isListening: false,
            microphoneError,
          });
        },
        () => {
          console.log('[App] Speech recognition ended');
          setGameState({ isListening: false });
        }
      );
    }
  };

  const handleSaveGame = async (): Promise<void> => {
    try {
      if (!gameState.currentPlayerState || !gameState.userProfile) {
        throw new Error('No active game session to save');
      }

      setSaveState({ isSaving: true, lastSaved: null, error: null });

      const sessionId = await databaseService.saveGameState({
        userProfile: gameState.userProfile,
        currentPlayerState: gameState.currentPlayerState,
        currentStorySegment: gameState.currentStorySegment,
        selectedCartridge: gameState.selectedCartridge,
        chatMessages: gameState.chatMessages,
        sessionId: gameState.currentSessionId || undefined,
        isMultiplayer: gameState.isMultiplayer,
        roomId: gameState.currentRoom?.id
      });

      setGameState({ currentSessionId: sessionId });
      console.log('[App] Game saved successfully:', sessionId);
      setSaveState({ isSaving: false, lastSaved: new Date(), error: null });
    } catch (error) {
      console.error('[App] Failed to save game:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setGameState({ error: `Failed to save game: ${errorMessage}` });
      setSaveState({ isSaving: false, lastSaved: null, error: errorMessage });
    }
  };

  const handleCreateInviteLink = async (): Promise<void> => {
    try {
      if (!gameState.currentSessionId) {
        // First save the game to get a session ID
        await handleSaveGame();
        if (!gameState.currentSessionId) {
          throw new Error('Failed to save game before creating invite link');
        }
      }

      const shareLink = await databaseService.createShareableGameLink(gameState.currentSessionId);
      console.log('[App] Invite link created:', shareLink);
      
      // Copy to clipboard if available
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(shareLink);
        console.log('[App] Invite link copied to clipboard');
      }
    } catch (error) {
      console.error('[App] Failed to create invite link:', error);
      setGameState({
        error: `Failed to create invite link: ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
    }
  };

  // Initialize all services on component mount
  useEffect(() => {
    const initServices = async () => {
      try {
        // Initialize speech service
        await speechService.init();
        console.log('[App] Speech service initialized');
        
        // Initialize session persistence
        sessionPersistenceService.onSessionRestore((snapshot) => {
          console.log('[App] Restoring session from snapshot');
          setGameState(snapshot.gameState);
        });
        
        sessionPersistenceService.onVisibilityChange((isVisible) => {
          if (isVisible) {
            console.log('[App] Page visible - resuming ecosystem');
            if (gameState.currentScreen === 'gameplay' && !ecosystemActive) {
              autonomousEcosystemService.startEcosystem();
              setEcosystemActive(true);
            }
          } else {
            console.log('[App] Page hidden - updating session persistence');
            sessionPersistenceService.updateCurrentSession(gameState);
          }
        });
        
        // Handle demo account authentication
        window.addEventListener('demo-auth-success', async (event: any) => {
          console.log('[App] Demo auth success event received');
          const demoUser = event.detail.user;
          
          // Create demo user profile
          const demoProfile: UserProfile = {
            id: demoUser.id,
            email: demoUser.email,
            displayName: demoUser.user_metadata.display_name,
            selectedArchetype: 'cyberpunk-hacker',
            level: 1,
            totalExperience: 0,
            availableSkillPoints: 0,
            unclaimedRewards: [],
            uid: demoUser.id
          };
          
          // Set the user and profile
          setSupabaseUser(demoUser);
          setGameState({
            userProfile: demoProfile,
            currentScreen: 'lobby',
          });
          
          setIsLoadingAuth(false);
          setIsProfileLoadingState(false);
          
          console.log('[App] Demo user profile created and set');
        });
        
        console.log('[App] All services initialized');
      } catch (error) {
        console.warn('[App] Failed to initialize services:', error);
      }
    };
    
    initServices();
    
    // Cleanup on unmount
    return () => {
      autonomousEcosystemService.stopEcosystem();
      soundscapeService.emergencyStop();
      safeTTSService.emergencyStop();
      setEcosystemActive(false);
    };
  }, []);

  // Check for shared game on app load
  useEffect(() => {
    const loadSharedGame = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const shareId = urlParams.get('share');
      
      if (shareId && supabaseUser) {
        try {
          console.log('[App] Loading shared game:', shareId);
          const sharedGameState = await databaseService.loadSharedGame(shareId);
          
          if (sharedGameState) {
            setGameState(() => ({
              ...sharedGameState,
              currentScreen: 'gameplay',
              currentSessionId: shareId,
            }));
            
            // Clear the URL parameter
            window.history.replaceState({}, document.title, window.location.pathname);
            console.log('[App] Shared game loaded successfully');
          } else {
            setGameState({ error: 'Shared game not found or not accessible' });
          }
        } catch (error) {
          console.error('[App] Failed to load shared game:', error);
          setGameState({
            error: `Failed to load shared game: ${error instanceof Error ? error.message : 'Unknown error'}`,
          });
        }
      }
    };

    if (supabaseUser && !isLoadingAuth) {
      loadSharedGame();
    }
  }, [supabaseUser, isLoadingAuth]);

  // Auth state debouncing to prevent rapid consecutive auth events
  const DEBOUNCE_AUTH_MS = 1000; // 1 second debounce
  const AUTH_TIMEOUT_MS = 10000; // 10 seconds timeout for auth operations
  
  const debounceAuthEvent = (eventType: string, userId: string, callback: () => void) => {
    const now = Date.now();
    
    // Check if this is a duplicate event within the debounce window
    if (lastAuthEvent && 
        lastAuthEvent.type === eventType && 
        lastAuthEvent.userId === userId && 
        now - lastAuthEvent.timestamp < DEBOUNCE_AUTH_MS) {
      console.log(`[App] Debouncing duplicate auth event: ${eventType} for user ${userId}`);
      return;
    }
    
    // Clear any existing auth timeout
    if (authTimeoutId) {
      clearTimeout(authTimeoutId);
      setAuthTimeoutId(null);
    }
    
    // Update last auth event tracking
    setLastAuthEvent({ type: eventType, userId, timestamp: now });
    
    // Set a timeout to prevent hanging auth states
    const timeoutId = setTimeout(() => {
      console.warn('[App] Auth operation timeout, forcing completion');
      setIsLoadingAuth(false);
      setIsHandlingAuth(false);
      setIsProfileLoadingState(false);
    }, AUTH_TIMEOUT_MS);
    
    setAuthTimeoutId(timeoutId);
    
    // Execute the callback
    callback();
  };

  // Show loading screen while checking authentication
  if (isLoadingAuth || isProfileLoadingState) {
    return <LoadingIndicator message="Loading..." />;
  }

  // Main app rendering logic
  switch (gameState.currentScreen) {
    case 'auth':
      return (
        <>
          <AuthScreen onAuthSuccess={async () => {
            console.log('[App] Auth success callback triggered');
            // The auth state change listener will handle the user sign-in
            // This callback just ensures we're not stuck in loading
            setIsLoadingAuth(false);
            setIsProfileLoadingState(false);
          }} />
          {import.meta.env.DEV && <DebugOpenRouter />}
          {import.meta.env.DEV && <AutonomyConsole />}
        </>
      );
    
    case 'userProfile':
      return (
        <UserProfileScreen
          onProfileComplete={handleProfileComplete}
          apiKeyStatus={gameState.apiKeyStatus}
          traits={CHARACTER_TRAITS}
          mindsets={MINDSETS}
          isLoadingAppearance={gameState.isLoadingAppearance}
          onSetLoadingAppearance={handleSetLoadingAppearance}
          onSetError={handleSetError}
          userProfile={gameState.userProfile}
          onUpdateProfile={handleUpdateProfileImage}
          currentUserId={supabaseUser?.id || null}
        />
      );
    
    case 'lobby':
      return (
        <LobbyScreen
          userProfile={gameState.userProfile}
          onCreateGame={handleCreateGame}
          onCreateCartridge={handleCreateCartridge}
          onJoinGame={handleJoinGame}
          onSignOut={handleSignOut}
        />
      );
    
    case 'cartridge_browser':
      return (
        <CartridgeBrowserScreen
          onSelectCartridge={handleSelectCartridge}
          onBack={handleBackToCartridges}
          onCreateCartridge={handleCreateCartridge}
        />
      );
    
    case 'cartridge_editor':
      return (
        <CartridgeEditorScreen 
          onBack={() => setGameState({ currentScreen: 'cartridge_browser' })}
          onCartridgeCreated={(cartridge) => {
            console.log('[App] New cartridge created:', cartridge);
            // Optionally handle the new cartridge (e.g., add to available cartridges)
          }}
        />
      );
    
    case 'mode_selection':
      return (
        <ModeSelectionScreen
          selectedCartridge={gameState.selectedCartridge!}
          onSelectSinglePlayer={handleSelectSinglePlayer}
          onSelectMultiplayer={handleSelectMultiplayer}
          onSelectGraphical={handleSelectGraphicalPreview}
          onBack={handleBackToCartridges}
        />
      );

    case 'graphical':
      return (
        <GraphicalMode onExit={handleExitGraphicalPreview} />
      );
    
    case 'continue_dialog':
      if (gameState.selectedCartridge?.playerProgress) {
        return (
          <ContinueOrNewDialog
            cartridgeName={gameState.selectedCartridge.name}
            progressInstances={gameState.selectedCartridge.playerProgress}
            onContinue={handleContinueGame}
            onNewGame={handleNewGameFromDialog}
            onCancel={handleCancelContinueDialog}
          />
        );
      }
      // If no progress, fallback to main screen
      setGameState({ currentScreen: 'main' });
      return null;
    
    case 'room_browser':
      if (gameState.userProfile && gameState.selectedCartridge) {
        return (
          <RoomBrowserScreen
            userProfile={gameState.userProfile}
            selectedCartridge={gameState.selectedCartridge}
            onCreateRoom={handleCreateRoom}
            onJoinRoom={handleJoinRoom}
            onBack={handleBackToModeSelection}
          />
        );
      }
      break;
    
    case 'main':
      if (gameState.userProfile) {
        return (
          <MainScreen
            onStartGame={async (archetype: Archetype) => {
              if (!gameState.userProfile || !gameState.selectedCartridge) {
                setGameState({ error: 'User profile or game cartridge not selected.' });
                return;
              }

              setGameState({ isLoading: true, error: null });
              try {
                // 1. Initialize Player State
                const initialPlayerState: PlayerState = {
                  userProfile: gameState.userProfile,
                  archetype,
                  location: gameState.selectedCartridge.worldSettings.initialLocation || 'a mysterious void',
                  status: 'active',
                  storyHistory: [],
                  currentStorySegment: null, // Will be filled by AI
                };

                // 2. Generate Initial Story Segment
                const initialPrompt = gameState.selectedCartridge.storyPrompts?.initialNarrative ||
                                     gameState.selectedCartridge.storyPrompts?.[0] ||
                                     `You are ${archetype.name}, ${archetype.description}. The adventure begins in ${initialPlayerState.location}.`;
                const gameHistorySummary = "The adventure begins."; 
                
                // Show loading state immediately
                setGameState({
                  isLoading: true,
                  error: null,
                  loadingMessage: 'Connecting to the dream realm...',
                });

                const storyResponse = await generateStorySegment(
                  initialPrompt,
                  "Embark on the adventure.", // Initial "choice" or action
                  gameHistorySummary,
                  archetype.description,
                  gameState.userProfile,
                  supabaseUser?.id
                );
                
                const initialStorySegment: StorySegment = {
                    narrative: storyResponse.narrative,
                    imagePrompt: storyResponse.image_prompt,
                    choices: storyResponse.choices,
                    companionMessages: storyResponse.companion_dialogue,
                    imageUrl: storyResponse.imageUrl || null
                };

                const initialChatMessages = processStoryDataToChatMessages(storyResponse);

                // Generate new instance ID for this game session
                const newInstanceId = `instance-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

                setGameState({
                  currentPlayerState: initialPlayerState,
                  currentStorySegment: initialStorySegment,
                  chatMessages: initialChatMessages,
                  currentScreen: 'gameplay',
                  isLoading: false,
                  currentInstanceId: newInstanceId,
                });

              } catch (error) {
                console.error("Error starting game:", error);
                setGameState({
                  isLoading: false,
                  error: 'Failed to start the dream. The spirits are restless. Please try again.',
                });
              }
            }}
            apiKeyStatus={gameState.apiKeyStatus}
            userProfile={gameState.userProfile}
            archetypes={gameState.selectedCartridge?.archetypes || ARCHETYPES}
            selectedCartridge={gameState.selectedCartridge}
            currentRoom={gameState.currentRoom}
            isMultiplayer={gameState.isMultiplayer}
          />
        );
      }
      break;
    
    case 'gameplay':
      // Remove strict requirement for currentStorySegment - component will initialize or load it
      if (gameState.userProfile && gameState.selectedCartridge) {
        return (
          <>
            <EnhancedGameplayScreen
              userProfile={gameState.userProfile}
              cartridgeId={gameState.selectedCartridge?.id || 'default'}
              cartridgeName={gameState.selectedCartridge?.name}
              instanceId={gameState.sessionId || gameState.currentSessionId}
              savedStorySegment={gameState.currentStorySegment}
              savedChatMessages={gameState.chatMessages}
              savedPlayerSession={gameState.currentPlayerState as any}
              onExit={() =>
                setGameState({
                  currentScreen: 'lobby',
                  currentPlayerState: null,
                  currentStorySegment: null,
                  chatMessages: [],
                })
              }
            />
            <SaveIndicator {...saveState} />
            
            {/* Trading Modal - guarded by feature flag */}
            {isFeatureEnabled('trading') && showTradingModal && gameState.userProfile && (
              <React.Suspense fallback={null}>
                <TradingModal
                  playerId={(gameState.userProfile as any).id}
                  playerInventory={(gameState.userProfile as any).inventory || []}
                  onClose={() => setShowTradingModal(false)}
                  onTradeComplete={(result: any) => {
                    console.log('[App] Trade completed:', result);
                    setShowTradingModal(false);
                  }}
                />
              </React.Suspense>
            )}

            {/* Character Chat Panel */}
            {showCharacterChat && gameState.userProfile && (
              <React.Suspense fallback={null}>
                <CharacterChatPanel
                  userProfile={gameState.userProfile}
                  currentLocation={gameState.currentPlayerState?.location || 'Neo-Veridia Central'}
                  isVisible={showCharacterChat}
                  onClose={() => setShowCharacterChat(false)}
                />
              </React.Suspense>
            )}

            {/* Always-on Global/Realm Chat Dock */}
            <GlobalChatDock
              userProfile={gameState.userProfile}
              realmId={gameState.selectedCartridge?.id || gameState.currentRoom?.id || null}
              currentLocation={gameState.currentPlayerState?.location || 'neo-tokyo-plaza'}
              recentNarrative={gameState.currentStorySegment?.narrative || ''}
              worldKarma={gameState.worldKarma || 0}
              companionsPresent={['JAX', 'Lyra']}
            />
          </>
        );
      }
      // If not ready for gameplay, could redirect to MainScreen or show loader/error
      if (gameState.isLoading) {
        return <LoadingIndicator message="Initializing dream sequence..." />;
      }
      // If there was an error during game start, show it or allow retry/exit.
      if (gameState.error) {
         return (
          <div className="flex flex-col items-center justify-center min-h-screen bg-slate-900 text-red-400 p-4">
            <p className="text-xl mb-4">Error entering dream: {gameState.error}</p>
            <button 
              onClick={() => setGameState({ currentScreen: 'main', error: null })}
              className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-slate-200"
            >
              Try Again
            </button>
          </div>
        );
      }
      // Fallback if somehow in gameplay state without necessary data and not loading/error
      return <LoadingIndicator message="Preparing your dream..." />;
    
    case 'combat':
      if (gameState.userProfile && supabaseUser) {
        return (
          <>
            <CombatScreen
              gameState={gameState}
              onNavigate={(screen) => setGameState({ currentScreen: screen as any })}
              onCombatEnd={() => {
                console.log('[App] Combat ended, checking for state restoration...');

                // CRITICAL FIX: Check if we came from graphical mode
                const hasGraphicalTransition = combatTransitionService.hasPendingTransition();

                if (hasGraphicalTransition) {
                  console.log('[App] ✅ Detected graphical mode transition, restoring state...');

                  // Restore state FIRST (this updates Zustand)
                  const restoredState = combatTransitionService.restoreAfterCombat();

                  if (restoredState) {
                    console.log('[App] ✅ State restored, navigating back to graphical mode');

                    // Navigate to mode-selection first, then to graphical
                    // This ensures proper component mounting
                    setGameState({ currentScreen: 'mode-selection' });

                    // Wait for next tick, then switch to graphical mode
                    setTimeout(() => {
                      setGameState({ currentScreen: 'graphical' });
                      console.log('[App] ✅ Successfully returned to graphical mode');
                    }, 100);

                    return;
                  } else {
                    console.error('[App] ❌ Failed to restore graphical state, falling back to gameplay');
                  }
                }

                // ORIGINAL LOGIC: Restore text mode state
                setGameState((state) => {
                  if (state.preCombatState) {
                    localStorage.removeItem('lucid_cyber_rpg_pre_combat_state');
                    return {
                      currentScreen: 'gameplay',
                      currentPlayerState: state.preCombatState.currentPlayerState,
                      selectedCartridge: state.preCombatState.selectedCartridge,
                      currentRoom: state.preCombatState.currentRoom,
                      storyHistory: state.preCombatState.storyHistory || state.storyHistory,
                      selectedArchetype:
                        state.preCombatState.selectedArchetype || state.selectedArchetype,
                      currentStorySegment: state.preCombatState.currentStorySegment,
                      currentSegmentIndex: state.preCombatState.currentSegmentIndex,
                      preCombatState: undefined,
                    };
                  }

                  const savedPreCombatState = localStorage.getItem(
                    'lucid_cyber_rpg_pre_combat_state'
                  );
                  if (savedPreCombatState) {
                    try {
                      const parsed = JSON.parse(savedPreCombatState);
                      localStorage.removeItem('lucid_cyber_rpg_pre_combat_state');

                      return {
                        currentScreen: 'gameplay',
                        currentPlayerState: parsed.currentPlayerState || state.currentPlayerState,
                        selectedCartridge: parsed.selectedCartridge,
                        currentRoom: parsed.currentRoom,
                        storyHistory: parsed.storyHistory || [],
                        selectedArchetype: parsed.selectedArchetype,
                        currentStorySegment: parsed.currentStorySegment,
                        currentSegmentIndex: parsed.currentSegmentIndex || 0,
                        preCombatState: undefined,
                      };
                    } catch (e) {
                      console.error('[App] Failed to restore pre-combat state from localStorage:', e);
                    }
                  }

                  console.warn('[App] No pre-combat state available, returning to main screen');
                  return { currentScreen: 'main', preCombatState: undefined };
                });
              }}
            />
            <SaveIndicator {...saveState} />
          </>
        );
      }
      return <LoadingIndicator message="Initializing combat matrix..." />;
    
    case 'feed':
      const FeedScreen = React.lazy(() => import('@/components/FeedScreen'));
      return (
        <React.Suspense fallback={<LoadingIndicator message="Loading dream feed..." />}>
          <FeedScreen
            userId={supabaseUser?.id}
            filters={{
              cartridgeId: gameState.selectedCartridge?.id
            }}
            onClose={() => setGameState({ currentScreen: 'gameplay' })}
          />
        </React.Suspense>
      );
    
    default:
      return (
        <>
          <LoadingIndicator message="Loading..." />
          {import.meta.env.DEV && <SentryTest />}
        </>
      );
  }
  
  return (
    <>
      {/* This will never be reached due to switch statement, but keeps TS happy */}
      <LoadingIndicator message="Loading..." />
      {import.meta.env.DEV && <SentryTest />}
      {import.meta.env.DEV && <AutonomyConsole />}
      {import.meta.env.DEV && React.createElement(React.lazy(() => import('./components/DebugOpenRouter').then(m => ({ default: m.DebugOpenRouter }))))}
    </>
  );
};

export default App;
